// The two entry points, and the branch behind them that decides which badge a
// piece is wearing.
//
// Only pieces with something to report get a badge. A board where every piece
// carries a label is the arrow soup PLAN §7.1 warns about; the rule here is
// that a badge means "this piece is doing something, or could be".

import { isTurretKind } from "src/engine/rules/industry/turret";
import {
  type Color,
  KING,
  pieceColor,
  pieceType,
  squareName
} from "src/engine/rules/board";
import type { GameState } from "src/engine/rules/commands";
import { armourLeft, disguiseLeft, hurtAt, pieceAt, platingAt, socksLeft } from "src/engine/rules/position";
import { plateWorthMarking } from "../plateMark";
import {
  isFoundryAt,
  isMining,
  refinesAt,
  machineType,
  type MachineType
} from "src/engine/rules/industry/machines";
import {
  bufferTotal,
  handoffAt,
  isArtifactKind,
  structureAt,
  structureSquares,
  dumpsAt
} from "src/engine/rules/industry/state";

import { pieceLabel } from "src/engine/rules/naming";
import type { BadgeCore, BadgeKind, PieceBadge } from "./types";
import { forgeFields, fuseOptions, upgradeOptions } from "./options";
import { foundryBadge, minerBadge, refineryBadge } from "./machines";
import { activity, despairOf } from "./activity";
import {
  artifactDetail,
  damagePip,
  storeDetail,
  trainDetail,
  turretDetail
} from "./structures";

/**
 * The badge a piece would wear, or null when it has nothing to say.
 *
 * Split out so that `pieceBadges` (the markers on the board, which only show
 * for pieces that are doing something) and `pieceDetail` (the shelf, which
 * must describe whatever you clicked on) are the same model rather than two
 * that drift.
 */
function badgeCoreFor(
  state: GameState,
  sq: number,
  owner: Color,
  _type: MachineType
): BadgeCore | null {
  // Mining first, and for any harvester: a king digging is a mine this turn
  // whatever else it could be, so its badge has to say so rather than report
  // an oven that will not run.
  if (isMining(state.position, state.industry, sq)) {
    const core = minerBadge(state, sq, owner);
    // A king on a heap is still a foundry, and forging is what it would do
    // *instead* of digging — so the recipes travel onto the miner badge
    // rather than waiting for it to step off the scrap.
    return isFoundryAt(state.position, state.industry, sq)
      ? { ...core, ...forgeFields(state, sq, owner) }
      : core;
  }

  // Then foundries, which is rooks *and* kings. Kings used to fall past this
  // to the refinery badge, which stays quiet when there is nothing in the
  // oven — so an idle king had no badge at all, and neither its recipes nor
  // its duty had anywhere to live.
  // **`isFoundryAt`, not `isFoundry`.** A crowned queen is a foundry and the
  // type alone cannot see it, so the shelf would have shown her a collector's
  // badge — no recipes, no "saving for", and no way to spend the card.
  if (isFoundryAt(state.position, state.industry, sq)) return foundryBadge(state, sq, owner);
  if (refinesAt(state.position, sq)) return refineryBadge(state, sq, owner);

  // A king under the diagnosis speaks up whatever else it is doing.
  //
  // Otherwise the one piece the countdown is about is the one piece with no
  // mark on it: a wave's king is *plain* — no oven, no hopper — so it falls
  // past every branch above and, holding nothing, past the one below too.
  const doomed = despairOf(state, sq, pieceType(pieceAt(state.position, sq)));
  if (doomed) {
    return {
      square: sq,
      owner,
      routes: false,
      dumps: false,
      handoff: handoffAt(state.industry, sq),
      kind: "carrier",
      short: "☠",
      title: doomed.mine ? "Your army has no way to win" : "This army has no way to win",
      detail:
        `Nothing it can reach adds up to a mating piece. It has ` +
        `${doomed.turnsLeft} ${doomed.turnsLeft === 1 ? "turn" : "turns"} to ` +
        `change that — a second real piece, or metal on ground it holds — ` +
        `before it lays down its arms.`
    };
  }

  // A status the board did to the piece speaks up whatever else is true.
  //
  // Same trap the despair badge above is written for: a plain pawn is not a
  // machine and holds nothing, so it falls past every branch here — and a
  // pawn is exactly what somebody freezes or what stands up at one health.
  // The pip would have been built and then had no badge to sit on.
  const armour = armourLeft(state.position, sq);
  const damage = hurtAt(state.position, sq);
  const held = bufferTotal(state.industry, sq, "in") + bufferTotal(state.industry, sq, "out");
  const socks = pieceType(pieceAt(state.position, sq)) === KING
    ? socksLeft(state.position, owner)
    : 0;
  // A costume is the same case again, and the commonest one: the piece worth
  // disguising is usually a pawn or a knight walking somewhere quietly, which
  // is exactly the piece that is not a machine and holds nothing.
  const costume = disguiseLeft(state.position, sq);
  // The same case a fourth time: a plated pawn earns no badge any other way.
  // Worn or extra only — every knight and rook is born plated (§31 K1).
  const plate = plateWorthMarking(state.position, sq) ? platingAt(state.position, sq) : 0;
  if (held === 0 && (armour > 0 || damage > 0 || socks > 0 || costume > 0 || plate > 0)) {
    return {
      square: sq,
      owner,
      routes: false,
      dumps: false,
      handoff: handoffAt(state.industry, sq),
      kind: "carrier",
      short:
        armour > 0 ? "⛨" : socks > 0 ? "♛" : damage > 0 ? "✚" : plate > 0 ? "🛡" : "🎭",
      title:
        armour > 0
          ? "Frozen"
          : socks > 0
            ? "Programmer Oil"
            : damage > 0
              ? "Hurt"
              : plate > 0
                ? "Armoured"
                : "Disguised",
      detail:
        armour > 0
          ? `Held in a Stasis Field: it cannot move, take, threaten or be ` +
            `taken. It still blocks a line.`
          : damage > 0
            ? `The next hit that reaches it kills it.`
            : plate > 0
              ? `${plate} armour. Turret fire and strikes have to chew through ` +
                `it before anything reaches the piece — but a capture ignores ` +
                `it. It regenerates a plate a round while the piece rests.`
              : `One army reads this piece as one of its own. It still moves, ` +
                `takes and blocks exactly as it always did.`
    };
  }

  // Anything else only speaks up when it is actually carrying something.
  if (held === 0) return null;
  return {
    square: sq,
    owner,
    routes: true,
    dumps: dumpsAt(state.industry, sq),
    handoff: handoffAt(state.industry, sq),
    kind: "carrier",
    short: `${held}`,
    title: `Carrying ${held} unit${held === 1 ? "" : "s"}`,
    detail:
      `Material rides with the piece. It drops as raw scrap if the piece ` +
      `is taken, so a loaded piece is a fat target.`
  };
}

export function pieceBadges(state: GameState): PieceBadge[] {
  const badges: PieceBadge[] = [];

  for (const sq of state.position.cellList) {
    const piece = pieceAt(state.position, sq);
    if (!piece) continue;
    const owner = pieceColor(piece);
    const core = badgeCoreFor(state, sq, owner, machineType(state.position, sq));
    if (!core) continue;
    const badge = { ...core, ...activity(state, sq, owner, true) };
    // A marker with nothing left to say is not drawn: an idle foundry reads
    // blank rather than "- / ?" on every rook and king.
    if (!badge.short && badge.pips.length === 0) continue;
    badges.push(badge);
  }

  // Buildings, from their own list — a structure's square is not a cell, so
  // the sweep above cannot reach one. Only when it has something to say:
  // material inside it, or a tap open in the direction that lets it out. A
  // badge on every warehouse on the board is the arrow soup §7.1 warns about.
  for (const sq of structureSquares(state.industry)) {
    const structure = structureAt(state.industry, sq)!;
    // **A building that has been hit says so, whatever kind it is** (§27).
    // Computed here rather than inside each of the four detail functions: it
    // is one fact about `Structure` and none of them differ about it.
    const wounded = damagePip(structure);
    /** The kind's own badge, with the wound in front of everything else. */
    const hurt = (badge: PieceBadge): PieceBadge => {
      if (wounded) badge.pips.unshift(wounded);
      return badge;
    };
    // **A turret always gets one.** The rule below is right for a warehouse —
    // a badge on every empty store is the arrow soup §7.1 rates the top risk —
    // and wrong for a gun, whose whole state is what it can do to whatever
    // walks past. It is also usually *empty* now that its appetite is the next
    // tier rather than a store's eighty, so the store rule would hide it
    // almost always.
    if (isTurretKind(structure.kind)) {
      badges.push(hurt(turretDetail(state, sq, structure)));
      continue;
    }
    // **An artifact structure always gets one too** (§26 F6), the turret's
    // argument: the store rule below is about empty warehouses, and a power's
    // address is never "empty" — it is on. It is also the one mark that tells
    // a Banner from a Relic while the two share the store's crate art.
    if (isArtifactKind(structure.kind)) {
      badges.push(hurt(artifactDetail(state, sq, structure)));
      continue;
    }
    // A train (§31 S7): its order or its riders, always.
    if (structure.train) {
      badges.push(hurt(trainDetail(sq, structure)));
      continue;
    }
    // A damaged one always gets a badge too: an empty warehouse being ground
    // down is exactly the thing the store rule would otherwise hide, and it is
    // the one state where the player has a turn or two to do something.
    if (structure.stock.length === 0 && structure.offer === 0 && !wounded) continue;
    badges.push(hurt(storeDetail(state, sq, structure)));
  }

  return badges;
}

/**
 * Everything about one piece, for the shelf.
 *
 * Unlike `pieceBadges` this never declines. A board marker is there to say
 * "something is happening here", so a quiet piece rightly has none — but the
 * shelf describes whatever you have clicked on, and a panel that went blank
 * for a pawn on an empty square would read as the selection not registering.
 * A piece with nothing to report gets a badge that says exactly that.
 */
export function pieceDetail(state: GameState, sq: number): PieceBadge | null {
  const structure = structureAt(state.industry, sq);
  if (structure) {
    // Three panels, because they are three different things: a gun, a
    // warehouse, and a power's address. `storeDetail` for an artifact kind
    // described a Banner as "holds 0 of 80 units" with taps under it — wrong
    // on every line, and the taps were a door the reducer has since barred.
    return isTurretKind(structure.kind)
      ? turretDetail(state, sq, structure)
      : isArtifactKind(structure.kind)
      ? artifactDetail(state, sq, structure)
      : structure.train
      ? trainDetail(sq, structure)
      : storeDetail(state, sq, structure);
  }

  const piece = pieceAt(state.position, sq);
  if (!piece) return null;

  const owner = pieceColor(piece);
  const core = badgeCoreFor(state, sq, owner, machineType(state.position, sq)) ?? {
    square: sq,
    owner,
    kind: "carrier" as BadgeKind,
    short: "",
    title: `${pieceLabel(state.position, sq)} · ${squareName(sq)}`,
    detail: "Carrying nothing and running nothing. Every piece can be handed material.",
    routes: true,
    dumps: dumpsAt(state.industry, sq),
    handoff: handoffAt(state.industry, sq)
  };

  // **A variant is not a factory job, so it hangs off every badge.** A
  // foundry's recipes belong to `foundryBadge`; a variant belongs to the piece,
  // whatever kind of badge it happens to be wearing. Omitted rather than empty
  // when there is none, so nothing renders for a king.
  const upgrades = upgradeOptions(state, sq, owner);
  // And a fuse (§31 P4), the same way: the piece's, listed once its partners stand by.
  const fuses = fuseOptions(state, sq, owner);
  return {
    ...core,
    ...activity(state, sq, owner),
    ...(upgrades.length > 0 ? { upgrades } : {}),
    ...(fuses.length > 0 ? { fuses } : {})
  };
}
